import { Schema } from "effect";
import { ProviderKind } from "./orchestration";

export const CODEX_REASONING_EFFORT_OPTIONS = ["max", "xhigh", "high", "medium", "low"] as const;
export type CodexReasoningEffort = (typeof CODEX_REASONING_EFFORT_OPTIONS)[number];

export const CodexModelOptions = Schema.Struct({
  reasoningEffort: Schema.optional(Schema.Literals(CODEX_REASONING_EFFORT_OPTIONS)),
  fastMode: Schema.optional(Schema.Boolean),
});
export type CodexModelOptions = typeof CodexModelOptions.Type;

export const ProviderModelOptions = Schema.Struct({
  codex: Schema.optional(CodexModelOptions),
});
export type ProviderModelOptions = typeof ProviderModelOptions.Type;

export type ModelOption = {
  readonly slug: string;
  readonly name: string;
  readonly description?: string;
  readonly recommendedFor?: string;
  readonly defaultReasoningEffort?: CodexReasoningEffort;
};

export const MODEL_OPTIONS_BY_PROVIDER = {
  codex: [
    {
      slug: "gpt-6-luna",
      name: "GPT-6 Luna",
      description: "Fast and affordable for general work",
      recommendedFor: "General",
      defaultReasoningEffort: "max",
    },
    {
      slug: "gpt-6.1-sol",
      name: "GPT-6.1 Sol",
      description: "Frontier capability for complex coding",
      recommendedFor: "Coding",
      defaultReasoningEffort: "high",
    },
  ],
} as const satisfies Record<ProviderKind, readonly ModelOption[]>;
export type ModelOptionsByProvider = typeof MODEL_OPTIONS_BY_PROVIDER;

export const LEGACY_MODEL_OPTIONS_BY_PROVIDER = {
  codex: [
    {
      slug: "gpt-5.5",
      name: "GPT-5.5 (legacy)",
      defaultReasoningEffort: "medium",
    },
    {
      slug: "gpt-5.6-luna",
      name: "GPT-5.6 Luna (legacy)",
      defaultReasoningEffort: "max",
    },
    {
      slug: "gpt-5.6-sol",
      name: "GPT-5.6 Sol (legacy)",
      defaultReasoningEffort: "high",
    },
    {
      slug: "gpt-5.6-terra",
      name: "GPT-5.6 Terra (legacy)",
      defaultReasoningEffort: "medium",
    },
    {
      slug: "gpt-5.4",
      name: "GPT-5.4 (legacy)",
      defaultReasoningEffort: "medium",
    },
  ],
} as const satisfies Record<ProviderKind, readonly ModelOption[]>;

type BuiltInModelSlug = ModelOptionsByProvider[ProviderKind][number]["slug"];
export type ModelSlug = BuiltInModelSlug | (string & {});

export const DEFAULT_MODEL_BY_PROVIDER = {
  codex: "gpt-6-luna",
} as const satisfies Record<ProviderKind, ModelSlug>;

export const MODEL_SLUG_ALIASES_BY_PROVIDER = {
  codex: {
    "6.1-sol": "gpt-6.1-sol",
    "6-sol": "gpt-6.1-sol",
    "gpt-6-sol": "gpt-6.1-sol",
    "6-luna": "gpt-6-luna",
    "gpt-5.6": "gpt-5.6-sol",
    "5.6": "gpt-5.6-sol",
    "5.6-sol": "gpt-5.6-sol",
    "5.6-terra": "gpt-5.6-terra",
    "5.6-luna": "gpt-5.6-luna",
    "5.5": "gpt-5.5",
    "5.4": "gpt-5.4",
  },
} as const satisfies Record<ProviderKind, Record<string, ModelSlug>>;

export const REASONING_EFFORT_OPTIONS_BY_PROVIDER = {
  codex: CODEX_REASONING_EFFORT_OPTIONS,
} as const satisfies Record<ProviderKind, readonly CodexReasoningEffort[]>;

export const DEFAULT_REASONING_EFFORT_BY_PROVIDER = {
  codex: "medium",
} as const satisfies Record<ProviderKind, CodexReasoningEffort | null>;
